import React, { useState } from "react";
import { format } from "date-fns";
import { Chip, Dialog, DialogContent, DialogTitle, IconButton, Tooltip } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import ColumnHeader from "../components/common/ColumnHeader";
import { getChipColor } from "./value";
import { dateSortComparator } from "../utils/date";
import { formatAdjustAmount } from "../utils/adjustBalance";

const dateColumn = (field, headerName) => ({
  field,
  headerName,
  minWidth: 200,
  filterable: false,
  flex: 3,
  sortable: false,
  valueGetter: (params) => params.row[field] || null,
  valueFormatter: (params) =>
    params.value && `${format(new Date(params.value), "MMMM d, y hh:mm a")}`,
  sortComparator: dateSortComparator,
});

const copyableColumn = ({ field, headerName, handleColumnClick, client, ...rest }) => ({
  field,
  headerName,
  sortable: false,
  renderHeader: () =>
    client ? (
      <ColumnHeader label={headerName} handleClick={() => handleColumnClick(field)} />
    ) : (
      headerName
    ),
  ...rest,
});

const withSortable = (columns, client) =>
  client ? columns.map((col) => ({ ...col, sortable: true })) : columns;

// Thumbnail for a SIM's QR activation link (stored in extra_data.qr_activation_link);
// clicking it opens the full-size QR code in a modal.
const QrCodeCell = ({ url }) => {
  const [open, setOpen] = useState(false);

  if (!url) return "-";

  return (
    <>
      <img
        src={url}
        alt="QR activation code"
        style={{ width: 32, height: 32, cursor: "pointer" }}
        onClick={() => setOpen(true)}
      />
      <Tooltip title="Copy QR Activation Link">
        <IconButton
          color="secondary"
          aria-label="copy qr activation link"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(url);
          }}
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="xs">
        <DialogTitle>SIM Activation QR Code</DialogTitle>
        <DialogContent>
          <img src={url} alt="QR activation code" style={{ width: "100%" }} />
        </DialogContent>
      </Dialog>
    </>
  );
};

/**
 * Per-operation DataGrid column-def factories for the shared BatchDetail
 * component. Columns vary meaningfully *between* operations but only
 * cosmetically *within* an operation across carriers - `showAreaZip` covers
 * the one meaningful per-carrier variation seen for change_number (Ultra).
 */
export const changeNumberColumns = ({
  handleColumnClick,
  client,
  showAreaZip = false,
  iccidHeaderName = "ICCID",
  newMsisdnHeaderName = "New MSISDN",
  oldMsisdnHeaderName = "Old MSISDN",
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 180,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    ...(showAreaZip
      ? [
          { field: "areacode", headerName: "Areacode", minWidth: 75, flex: 1, sortable: false },
          { field: "zipcode", headerName: "Zipcode", minWidth: 75, flex: 1, sortable: false },
        ]
      : []),
    copyableColumn({
      field: "new_msisdn",
      headerName: newMsisdnHeaderName,
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    copyableColumn({
      field: "old_msisdn",
      headerName: oldMsisdnHeaderName,
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const changeNumberWorkaroundColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "old_iccid",
      headerName: "Old ICCID",
      handleColumnClick,
      client,
      minWidth: 180,
      flex: 3,
    }),
    copyableColumn({
      field: "iccid",
      headerName: "New ICCID",
      handleColumnClick,
      client,
      minWidth: 180,
      flex: 3,
    }),
    copyableColumn({
      field: "old_msisdn",
      headerName: "Old MSISDN",
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    copyableColumn({
      field: "new_msisdn",
      headerName: "New MSISDN",
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 150,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// showTrialCount/showImei cover Cricket + Verizon (which surface a retry
// trial-count chip and device IMEI not shown by other carriers' activate
// pages); showAreacode/showZipcode cover the inverse (Cricket has neither,
// Verizon has zipcode only - sourced from either the top-level field or
// extra_data.zipcode).
export const activateColumns = ({
  handleColumnClick,
  client,
  showAreacode = true,
  showZipcode = true,
  showTrialCount = false,
  showImei = false,
  showPlan = false,
  iccidHeaderName = "ICCID",
  msisdnHeaderName = "MSISDN",
}) => {
  const statusColumn = {
    field: "status",
    headerName: "Status",
    minWidth: 120,
    flex: 2,
    sortable: false,
    renderCell: (params) => (
      <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
    ),
  };

  const trialCountColumn = {
    field: "trial_count",
    headerName: "Trials",
    minWidth: 50,
    flex: 1,
    sortable: false,
    renderCell: (params) => (
      <Chip label={`${params.row.trial_count}`} size="small" color="default" />
    ),
  };

  const msisdnColumn = copyableColumn({
    field: "msisdn",
    headerName: msisdnHeaderName,
    handleColumnClick,
    client,
    minWidth: 100,
    flex: 2,
  });

  const imeiColumn = copyableColumn({
    field: "extra_data.imei",
    headerName: "IMEI",
    handleColumnClick,
    client,
    minWidth: 100,
    flex: 2,
    valueGetter: (params) => params.row?.extra_data?.imei,
  });

  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    ...(showTrialCount ? [statusColumn, trialCountColumn, msisdnColumn] : [msisdnColumn, statusColumn]),
    ...(showImei ? [imeiColumn] : []),
    ...(showPlan
      ? [{ field: "plan_id", headerName: "Plan ID", flex: 1, sortable: false }, { field: "plan", headerName: "Plan", minWidth: 150, flex: 3, sortable: false }]
      : []),
    ...(showAreacode
      ? [{ field: "areacode", headerName: "Areacode", minWidth: 75, flex: 1, sortable: false }]
      : []),
    ...(showZipcode
      ? [
          {
            field: "zipcode",
            headerName: "Zipcode",
            minWidth: 75,
            flex: 1,
            sortable: false,
            valueGetter: (params) => params.row.zipcode || params.row?.extra_data?.zipcode,
          },
        ]
      : []),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const changeSimColumns = ({
  handleColumnClick,
  client,
  newIccidHeaderName = "New ICCID",
  oldIccidHeaderName = "Old ICCID",
  msisdnHeaderName = "MSISDN",
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: newIccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    copyableColumn({
      field: "old_iccid",
      headerName: oldIccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    copyableColumn({
      field: "msisdn",
      headerName: msisdnHeaderName,
      handleColumnClick,
      client,
      minWidth: 150,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// NOTE: field is "iccid" but headerName/copy label is "MSISDN" - matches the
// existing (pre-refactor) Deactivate/Restore list behavior exactly.
// iccidHeaderName defaults to "MSISDN" to match Tmobilej's original (pre-refactor)
// label for this field - other carriers label the same underlying `iccid`
// column differently (e.g. "ICCID", "MDN", "Phone Number") and must override it.
export const deactivateColumns = ({ handleColumnClick, client, iccidHeaderName = "MSISDN" }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// Same shape/quirk as deactivateColumns (field "iccid", labeled "MSISDN").
export const restoreColumns = deactivateColumns;

export const pauseColumns = ({ handleColumnClick, client, iccidHeaderName = "ICCID" }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 120,
      flex: 2,
    }),
    { field: "iccid", headerName: iccidHeaderName, minWidth: 200, flex: 2, sortable: false },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// Same shape as pauseColumns.
export const unpauseColumns = pauseColumns;

// Reset rows have no msisdn column, only iccid (the MDN, for PWG).
export const resetColumns = (ctx) =>
  pauseColumns(ctx).filter((column) => column.field !== "msisdn");

// showPlan (default) covers Tmobilej/Ultra/PWG's plan-based portin;
// showAddress covers VS's account/address-based portin authorization fields
// instead of a plan.
export const portinColumns = ({
  handleColumnClick,
  client,
  showPlan = true,
  showAddress = false,
  iccidHeaderName = "ICCID",
  msisdnHeaderName = "MSISDN",
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    ...(showPlan
      ? [
          { field: "plan_id", headerName: "Plan ID", flex: 1, sortable: false },
          { field: "plan", headerName: "Plan", minWidth: 250, flex: 4, sortable: false },
        ]
      : []),
    ...(showAddress
      ? [
          { field: "name", headerName: "Name", minWidth: 150, flex: 2, sortable: false },
          { field: "account_number", headerName: "Account Number", minWidth: 150, flex: 2, sortable: false },
          { field: "street", headerName: "Street", minWidth: 200, flex: 3, sortable: false },
          { field: "city", headerName: "City", minWidth: 120, flex: 2, sortable: false },
          { field: "state", headerName: "State", minWidth: 80, flex: 1, sortable: false },
        ]
      : []),
    { field: "zipcode", headerName: "Zipcode", minWidth: 75, flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: msisdnHeaderName,
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// showPlan (default) covers Tmobilej's plan_name column; showWebhook covers
// VS, which shows a webhook field instead of a plan.
export const updateNumberColumns = ({
  handleColumnClick,
  client,
  showPlan = true,
  showWebhook = false,
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    ...(showPlan
      ? [
          {
            field: "plan_name",
            headerName: "Plan",
            minWidth: 150,
            flex: 2,
            sortable: false,
            valueGetter: (params) => params.row.plan_name || "-",
          },
        ]
      : []),
    ...(showWebhook
      ? [{ field: "webhook", headerName: "Webhook", minWidth: 200, flex: 3, sortable: false }]
      : []),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const mmsColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 120,
      flex: 2,
    }),
    {
      field: "enable",
      headerName: "MMS",
      minWidth: 100,
      flex: 1,
      sortable: false,
      valueGetter: (params) => (params.row.enable ? "Enable" : "Disable"),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// "Clear Status" (Tmobilej) reuses the reset operation's data shape (iccid + status).
export const clearStatusColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: "ICCID",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const filterGroupAssignColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 120,
      flex: 2,
    }),
    {
      field: "subscriber_id",
      headerName: "Subscriber ID",
      minWidth: 170,
      flex: 3,
      sortable: false,
      valueGetter: (params) => params.row.subscriber_id || "-",
    },
    { field: "filter_type", headerName: "Filter Type", minWidth: 120, flex: 2, sortable: false },
    {
      field: "selected_group_names",
      headerName: "Group Names",
      minWidth: 220,
      flex: 3,
      sortable: false,
      valueGetter: (params) => (params.row.selected_group_names || []).join(", "),
    },
    {
      field: "selected_group_ids",
      headerName: "Group IDs",
      minWidth: 180,
      flex: 3,
      sortable: false,
      valueGetter: (params) => (params.row.selected_group_ids || []).join(", "),
    },
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    { field: "batch_id", headerName: "Batch ID", minWidth: 150, flex: 2, sortable: false },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const orderInfoColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
      renderHeader: () =>
        client ? (
          <ColumnHeader label="Status" handleClick={() => handleColumnClick("status")} />
        ) : (
          "Status"
        ),
    },
    copyableColumn({
      field: "order_id",
      headerName: "Order ID",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 2,
    }),
    {
      field: "order_status",
      headerName: "Order Status",
      minWidth: 150,
      flex: 2,
      sortable: false,
      renderCell: (params) =>
        params.row.order_status ? (
          <Chip label={params.row.order_status} size="small" color={getChipColor(params.row.order_status)} />
        ) : (
          "-"
        ),
      renderHeader: () =>
        client ? (
          <ColumnHeader label="Order Status" handleClick={() => handleColumnClick("order_status")} />
        ) : (
          "Order Status"
        ),
    },
    dateColumn("order_date", "Order Date"),
    copyableColumn({
      field: "order_type",
      headerName: "Order Type",
      handleColumnClick,
      client,
      minWidth: 150,
      flex: 2,
    }),
    copyableColumn({
      field: "order_status_message",
      headerName: "Order Message",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

const extraDataColumn = ({ field, headerName, handleColumnClick, client, boolValue = false }) =>
  copyableColumn({
    field,
    headerName,
    handleColumnClick,
    client,
    minWidth: 200,
    flex: 3,
    valueGetter: (params) => {
      const key = field.split(".")[1];
      const value = params.row?.extra_data?.[key];
      return value ?? (boolValue ? false : "-");
    },
  });

// sim_info columns for Tmobilej, which surfaces extra VoIP-routing extra_data
// fields not shown for other carriers' sim_info pages. showIccid/showWebhook
// cover VS, which has no iccid column but does show extra_data.messaging.Webhook.
export const simInfoColumns = ({
  handleColumnClick,
  client,
  showTmobilejFields = false,
  showIccid = true,
  showWebhook = false,
  showAccountNumber = false,
  showDateActivated = false,
  showPlan = false,
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    ...(showIccid
      ? [
          copyableColumn({
            field: "iccid",
            headerName: "ICCID",
            handleColumnClick,
            client,
            minWidth: 200,
            flex: 3,
          }),
        ]
      : []),
    copyableColumn({
      field: "status",
      headerName: "Status",
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    }),
    ...(showWebhook
      ? [
          {
            field: "extra_data.messaging.Webhook",
            headerName: "Webhook",
            minWidth: 200,
            flex: 2,
            sortable: false,
            valueGetter: (params) => params.row?.extra_data?.messaging?.Webhook || "-",
          },
        ]
      : []),
    ...(showAccountNumber
      ? [
          copyableColumn({
            field: "account_number",
            headerName: "Account Number",
            handleColumnClick,
            client,
            minWidth: 150,
            flex: 2,
          }),
        ]
      : []),
    ...(showDateActivated ? [dateColumn("date_activated", "Date Activated")] : []),
    ...(showPlan
      ? [{ field: "plan", headerName: "Plan", minWidth: 150, flex: 2, sortable: false }]
      : []),
    ...(showTmobilejFields
      ? [
          extraDataColumn({
            field: "extra_data.classification",
            headerName: "Classification",
            handleColumnClick,
            client,
          }),
          extraDataColumn({
            field: "extra_data.message_route_id",
            headerName: "Message Route ID",
            handleColumnClick,
            client,
          }),
          extraDataColumn({
            field: "extra_data.message_enabled",
            headerName: "Message Enabled",
            handleColumnClick,
            client,
          }),
          extraDataColumn({
            field: "extra_data.voice_route_id",
            headerName: "Voice Route ID",
            handleColumnClick,
            client,
          }),
          extraDataColumn({
            field: "extra_data.voice_enabled",
            headerName: "Voice Enabled",
            handleColumnClick,
            client,
            boolValue: true,
          }),
          extraDataColumn({
            field: "extra_data.forwarding_number",
            headerName: "Forwarding Number",
            handleColumnClick,
            client,
          }),
          extraDataColumn({
            field: "extra_data.mms_message_enabled",
            headerName: "MMS Enabled",
            handleColumnClick,
            client,
            boolValue: true,
          }),
          extraDataColumn({
            field: "extra_data.imei",
            headerName: "IMEI",
            handleColumnClick,
            client,
          }),
          extraDataColumn({
            field: "extra_data.port_out_pin",
            headerName: "Port Out PIN",
            handleColumnClick,
            client,
          }),
        ]
      : []),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
    ...(showTmobilejFields
      ? [
          copyableColumn({
            field: "extra_data.qr_activation_link",
            headerName: "QR Code",
            handleColumnClick,
            client,
            minWidth: 180,
            flex: 2,
            renderCell: (params) => (
              <QrCodeCell url={params.row?.extra_data?.qr_activation_link} />
            ),
          }),
        ]
      : []),
  ];

  return withSortable(columns, client);
};

export const changePlanColumns = ({ handleColumnClick, client, iccidHeaderName = "ICCID" }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    {
      field: "trial_count",
      headerName: "Trials",
      minWidth: 50,
      flex: 1,
      sortable: false,
      renderCell: (params) => <Chip label={`${params.row.trial_count}`} size="small" color="default" />,
    },
    { field: "plan_id", headerName: "Plan ID", flex: 1, sortable: false },
    { field: "plan", headerName: "Plan", minWidth: 250, flex: 4, sortable: false },
    { field: "old_plan_id", headerName: "Old Plan ID", flex: 1, sortable: false },
    { field: "old_plan", headerName: "Old Plan", minWidth: 250, flex: 4, sortable: false },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const adjustBalanceColumns = ({ handleColumnClick, client, iccidHeaderName = "ESN" }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    copyableColumn({
      field: "msisdn",
      headerName: "MDN",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    {
      field: "trial_count",
      headerName: "Trials",
      minWidth: 50,
      flex: 1,
      sortable: false,
      renderCell: (params) => <Chip label={`${params.row.trial_count}`} size="small" color="default" />,
    },
    { field: "balance_type", headerName: "Balance Type", minWidth: 130, flex: 2, sortable: false },
    {
      field: "amount",
      headerName: "Amount",
      minWidth: 110,
      flex: 2,
      sortable: false,
      renderCell: (params) => formatAdjustAmount(params.row.balance_type, params.row.amount),
    },
    copyableColumn({
      field: "subscription_id",
      headerName: "Subscription ID",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const hotlineColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: "ICCID",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 100,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const wifiCallingColumns = ({ handleColumnClick, client, iccidHeaderName = "ICCID" }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: iccidHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    { field: "zipcode", headerName: "Zipcode", minWidth: 75, flex: 1, sortable: false },
    {
      field: "enable",
      headerName: "Enable",
      flex: 1,
      sortable: false,
      valueGetter: (params) => (params.row.enable ? "True" : "False"),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// operation param ("imsi_create"/"imsi_delete") is used by the page to build
// the row-click path, not by the columns themselves.
export const imsiColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "imsi",
      headerName: "IMSI",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// Field is "iccid" but headerName/copy label is "IMSI" - matches the existing
// (pre-refactor) IMSIReset list behavior exactly. Status color lookup
// lowercases the raw value first (also matches original).
export const imsiResetColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "iccid",
      headerName: "IMSI",
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip
          label={params.row.status}
          size="small"
          color={getChipColor(params.row.status?.toLowerCase())}
        />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// Field is "msisdn" but headerName/copy label is "ICCID" - matches the
// existing (pre-refactor) IT Refill list behavior exactly.
// msisdnHeaderName defaults to "ICCID" to match IT/Refill's existing
// (pre-refactor) quirk of labeling the msisdn field "ICCID"; Cricket/Verizon
// Refill label it "MSISDN" and additionally show a retry trial-count chip.
export const refillColumns = ({
  handleColumnClick,
  client,
  msisdnHeaderName = "ICCID",
  showTrialCount = false,
}) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: msisdnHeaderName,
      handleColumnClick,
      client,
      minWidth: 200,
      flex: 3,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    ...(showTrialCount
      ? [
          {
            field: "trial_count",
            headerName: "Trials",
            minWidth: 50,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
              <Chip label={`${params.row.trial_count}`} size="small" color="default" />
            ),
          },
        ]
      : []),
    copyableColumn({
      field: "transaction_id",
      headerName: "Transaction ID",
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

export const portoutColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 100,
      flex: 2,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    {
      field: "port_status",
      headerName: "Port Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.port_status || "N/A"} size="small" color={getChipColor(params.row.port_status)} />
      ),
    },
    copyableColumn({
      field: "account_number",
      headerName: "Account Number",
      handleColumnClick,
      client,
      minWidth: 120,
      flex: 2,
    }),
    copyableColumn({
      field: "account_password",
      headerName: "Pin",
      handleColumnClick,
      client,
      minWidth: 120,
      flex: 2,
    }),
    dateColumn("effective_date", "Effective Date"),
    dateColumn("expiration_date", "Expiration Date"),
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};

// Tmobilej portout stores its PIN in extra_data.port_out_pin (submitted per
// line, not the account_number/account_password columns IT's portout uses),
// so it needs its own column set rather than the generic portoutColumns.
export const tmobilejPortoutColumns = ({ handleColumnClick, client }) => {
  const columns = [
    { field: "id", headerName: "ID", flex: 1, sortable: false },
    copyableColumn({
      field: "msisdn",
      headerName: "MSISDN",
      handleColumnClick,
      client,
      minWidth: 120,
      flex: 2,
    }),
    extraDataColumn({
      field: "extra_data.port_out_pin",
      headerName: "Pin",
      handleColumnClick,
      client,
    }),
    {
      field: "status",
      headerName: "Status",
      minWidth: 120,
      flex: 2,
      sortable: false,
      renderCell: (params) => (
        <Chip label={params.row.status} size="small" color={getChipColor(params.row.status)} />
      ),
    },
    dateColumn("created_at", "Created At"),
    dateColumn("last_updated", "Last Updated"),
  ];

  return withSortable(columns, client);
};
